import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Inbox, FileText } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";

export default function AdminShell({ title, children, action }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = [
    { id: "leads", label: "Leads", to: "/admin/leads", Icon: Inbox },
    { id: "posts", label: "Posts", to: "/admin/posts", Icon: FileText },
  ];

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text-1)]">
      <header className="sticky top-0 z-30 border-b border-[var(--border-dim)] bg-[var(--bg)]/85 backdrop-blur">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="full" />
              <span className="overline ml-2 hidden md:inline">/ console</span>
            </Link>

            <nav className="flex items-center gap-1">
              {tabs.map(({ id, label, to, Icon }) => {
                const active = location.pathname.startsWith(to);
                return (
                  <Link
                    key={id}
                    to={to}
                    data-testid={`admin-tab-${id}`}
                    className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-full font-mono-ui text-[11px] tracking-[0.22em] uppercase transition-colors ${
                      active
                        ? "bg-[var(--surface)] text-[var(--text-1)]"
                        : "text-[var(--text-3)] hover:text-[var(--text-1)]"
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <span className="hidden sm:inline font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)]">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={doLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--border-dim)] hover:border-[var(--vermilion)] hover:text-[var(--vermilion)] font-mono-ui text-[10px] tracking-[0.22em] uppercase transition-colors"
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">
            {title}
          </h1>
          {action}
        </div>
        {children}
      </main>
    </div>
  );
}
